'use client'

import { useState } from 'react'

type LiteYouTubeProps = {
  id: string
  title?: string
  /** Called the first time the user starts the video (e.g. to pause a carousel). */
  onPlay?: () => void
}

/**
 * Lightweight YouTube "facade": shows the real video thumbnail with a play
 * button and only mounts the heavy iframe player on click. This guarantees a
 * visible preview (the JS-API embed can render a blank/dark frame) and avoids
 * loading YouTube's player JS until the user actually wants to watch.
 */
const LiteYouTube = ({ id, title, onPlay }: LiteYouTubeProps) => {
  const [playing, setPlaying] = useState(false)

  if (!id) return null

  const handlePlay = () => {
    setPlaying(true)
    onPlay?.()
  }

  return (
    <div className='relative aspect-video w-full overflow-hidden rounded-lg bg-black'>
      {playing ? (
        <iframe
          allowFullScreen
          className='absolute inset-0 size-full'
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={title || 'YouTube video player'}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
          referrerPolicy='strict-origin-when-cross-origin'
        />
      ) : (
        <button
          type='button'
          aria-label={title ? `Play video: ${title}` : 'Play video'}
          className='group absolute inset-0 size-full'
          onClick={handlePlay}
        >
          {/* eslint-disable-next-line @next/next/no-img-element -- external YouTube thumbnail facade; the Next optimizer adds no value for a static YT thumb */}
          <img
            src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
            alt={title || 'Video thumbnail'}
            loading='lazy'
            className='absolute inset-0 size-full object-cover'
          />
          <span className='absolute inset-0 grid place-items-center'>
            <span className='grid size-16 place-items-center rounded-full bg-black/60 transition group-hover:bg-red-600'>
              <svg aria-hidden viewBox='0 0 24 24' className='size-8 translate-x-[2px] fill-white'>
                <path d='M8 5v14l11-7z' />
              </svg>
            </span>
          </span>
        </button>
      )}
    </div>
  )
}

export default LiteYouTube
